import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useInstallStore, isStandaloneMode } from '@/lib/install';

/**
 * Bottom-fixed popup that invites the user to install the PWA.
 *
 * Behavior:
 *  - Shows on EVERY page load (no 7-day localStorage cooldown) as long as
 *    the app is not already installed on the device.
 *  - The X (close) button hides it only for the current session (state-only).
 *  - "Installa" triggers the native prompt on Chrome/Edge, or opens the
 *    detailed step-by-step dialog on iOS Safari.
 *  - "Come si fa?" opens the detailed dialog with instructions for
 *    iOS / Android / Desktop.
 */
export const InstallPWAPrompt = () => {
  const { setDeferredPrompt, clearDeferredPrompt, openDialog, deferredPrompt } = useInstallStore();
  const [visible, setVisible] = useState(false);
  const [dismissedSession, setDismissedSession] = useState(false);

  useEffect(() => {
    if (isStandaloneMode()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      clearDeferredPrompt();
    };
    window.addEventListener('appinstalled', onInstalled);

    // Always show the popup after a short delay if the app isn't already
    // installed on the device. Requested behavior: appear on every visit,
    // dismiss only for the current session via the X button.
    const showTimer = setTimeout(() => setVisible(true), 1500);

    return () => {
      clearTimeout(showTimer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [setDeferredPrompt, clearDeferredPrompt]);

  // When the popup is visible, push page content upward so nothing is
  // permanently hidden behind it. The extra padding is set on <body> so it
  // works uniformly across every route.
  useEffect(() => {
    if (visible && !dismissedSession) {
      document.body.classList.add('has-install-banner');
    } else {
      document.body.classList.remove('has-install-banner');
    }
    return () => document.body.classList.remove('has-install-banner');
  }, [visible, dismissedSession]);

  const dismiss = () => {
    // Session-only dismiss. On the next page load / refresh the popup
    // will appear again (as requested).
    setVisible(false);
    setDismissedSession(true);
  };

  const install = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice && choice.outcome === 'accepted') {
          setVisible(false);
        }
        clearDeferredPrompt();
        return;
      } catch (e) {
        // fall through to detailed dialog
      }
    }
    openDialog();
  };

  if (!visible || dismissedSession) return null;

  return (
    <div
      className="fixed z-[500] bottom-0 left-0 right-0 pb-[env(safe-area-inset-bottom)] px-3 sm:px-4 pt-2 pointer-events-none"
      role="dialog"
      aria-live="polite"
      data-testid="pwa-install-prompt"
    >
      <div className="mx-auto w-full max-w-md pointer-events-auto">
        <div className="rounded-2xl border border-[--brand-border] bg-white shadow-[var(--shadow-xl)] p-3 sm:p-4 flex items-start gap-3 relative">
          <img
            src="/icon-192.png"
            alt="MōMA Pokè"
            className="h-11 w-11 rounded-xl shrink-0"
          />
          <div className="flex-1 min-w-0 pr-6">
            <div className="font-display font-bold text-sm text-[--brand-primary] flex items-center gap-1">
              <Smartphone size={14} /> Scarica l&apos;app MōMA • Pokè
            </div>
            <div className="text-xs text-[--brand-muted] mt-0.5">
              Aggiungila alla home per ordinare più velocemente.
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button
                size="sm"
                onClick={install}
                className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover] text-white h-8 px-3 text-xs"
                data-testid="pwa-install-button"
              >
                <Download size={12} className="mr-1" />
                {deferredPrompt ? 'Installa' : 'Come si fa?'}
              </Button>
              {deferredPrompt && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={openDialog}
                  className="h-8 px-2 text-xs text-[--brand-primary]"
                  data-testid="pwa-help-button"
                >
                  Guida
                </Button>
              )}
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Chiudi"
            className="absolute top-2 right-2 p-1 rounded-md text-[--brand-muted] hover:text-[--brand-fg] hover:bg-[--brand-surface-2] transition"
            data-testid="pwa-dismiss-button"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWAPrompt;
